/**
 * @file    DrawerHeader.tsx
 * @scope   VIEW
 * @summary Top area of the drawer: breadcrumb navigation plus the Show/Hide handle.
 * @consumes TabNavigator, DiyDrawerMainTab
 * @produces JSX only
 * @sideEffects None — every interaction is delegated through props.
 * @notes Deliberately world-agnostic: subtype navigation rules arrive pre-computed via
 *        `onNavigate` / `maxCrumbs` (see DiyWidgetEngine/plugins/useEnginePlugins.ts).
 */
import React from 'react';
import TabNavigator from 'components/TabNavigator/TabNavigator';
import { DiyDrawerMainTab } from '../../common/constants/tabs';

export interface DrawerHeaderProps {
  /** Tab currently displayed by the drawer. */
  currentTab: any;
  /** Invoked by the TabNavigator with the target tab and the tabs it popped on the way. */
  onNavigate: (tab: any, poppedTabs: any[]) => void;
  /** Breadcrumb depth for the current tab. */
  maxCrumbs: number;
  /** Whether the drawer body is expanded. */
  showDetail: boolean;
  /** Toggles the drawer body. */
  onToggleDetail: () => void;
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  currentTab,
  onNavigate,
  maxCrumbs,
  showDetail,
  onToggleDetail,
}) => (
  <>
    <div className="diy-drawer-title">
      <TabNavigator
        currentTab={currentTab}
        onNavigate={onNavigate}
        initialTab={DiyDrawerMainTab}
        maxCrumbs={maxCrumbs}
      />
    </div>

    <div className="diy-drawer-handle" onClick={onToggleDetail}>
      <div
        className="diy-drawer-handle-icon"
        data-testid="showHideIcon"
        style={{ rotate: 'revert' }}
      >
        {showDetail ? 'Hide' : 'Show'}
      </div>
    </div>
  </>
);

export default React.memo(DrawerHeader);
